import { createFileRoute } from "@tanstack/react-router";
import { useLocale, pickLocalized, DEFAULT_LOCALE } from "@/lib/i18n";
import { pageMeta, ogImageUrl, websiteLd, JsonLd } from "@/lib/seo";

import { fetchHomepage } from "@/lib/queries/homepage";
import { HeroSection } from "@/components/home/HeroSection";
import { getTurnstileSiteKey } from "@/lib/contact.functions";
import { CorePromiseSection } from "@/components/home/CorePromiseSection";
import { SanitySections } from "@/components/home/SanitySections";
import { TestimonialsSection } from "@/components/home/TestimonialsSection";

export const Route = createFileRoute("/")({
  component: Index,
  // Sanity homepage content + Turnstile sitekey (runtime env) for the hero waitlist form.
  loader: async () => {
    const [{ siteKey }, home] = await Promise.all([getTurnstileSiteKey(), fetchHomepage()]);
    return { siteKey, home };
  },
  staleTime: 60_000,
  head: ({ loaderData }) => {
    const home = loaderData?.home;
    // The hero may be the localized variant — og falls back to whichever is set.
    const heroImg = home?.heroHasLocalizedImage
      ? (home?.heroLocalizedImage?.en ?? home?.heroLocalizedImage?.fr)
      : home?.heroImage;
    return {
      ...pageMeta({
        title:
          pickLocalized(home?.seo?.metaTitle, DEFAULT_LOCALE) ||
          "Matchamind — A steadier mind, in minutes a day",
        description:
          pickLocalized(home?.seo?.metaDescription, DEFAULT_LOCALE) ||
          "Matchamind combines micro-journaling and AI to help you transform your inner dialogue and move forward with more clarity and calm.",
        path: "/",
        ogImage: ogImageUrl(home?.seo?.ogImage ?? heroImg),
      }),
      // Turnstile is only needed by the hero waitlist form — don't ship the
      // third-party script when that CTA is hidden in Sanity.
      scripts: home?.heroCTAs?.joinWaitlist?.visible
        ? [
            {
              src: "https://challenges.cloudflare.com/turnstile/v0/api.js",
              async: true,
              defer: true,
            },
          ]
        : [],
    };
  },
});

function Index() {
  const locale = useLocale();
  const { siteKey, home: data } = Route.useLoaderData();

  const home = data ?? {};

  return (
    <div className="flex flex-col gap-12 md:gap-20 lg:gap-24">
      <JsonLd data={websiteLd} />
      <HeroSection home={home} locale={locale} siteKey={siteKey} />
      <CorePromiseSection locale={locale} />
      <SanitySections
        sections={home.sections}
        learnMorePages={home.learnMorePages}
        locale={locale}
      />
      <TestimonialsSection testimonials={home.testimonials} locale={locale} />
    </div>
  );
}
